import { Link } from 'react-router-dom';
import { Dumbbell, LogOut, Sun, Moon, Heart, BookOpen, Scale, Target } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';

export function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-xs border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 text-primary-600 dark:text-primary-400 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-lg"
            aria-label="Treenipäiväkirja - Siirry etusivulle"
          >
            <Dumbbell className="h-8 w-8" aria-hidden="true" />
            <span className="font-bold text-xl hidden sm:block">Treenipäiväkirja</span>
          </Link>

          {/* Desktop navigation */}
          {isAuthenticated && (
            <nav className="hidden md:flex items-center gap-6" aria-label="Päänavigaatio">
              <Link
                to="/"
                className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium rounded-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                Treenit
              </Link>
              <Link
                to="/programs"
                className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium flex items-center gap-1 rounded-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <BookOpen className="h-4 w-4" aria-hidden="true" />
                Ohjelmat
              </Link>
              <Link
                to="/kyykkyohjelma"
                className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium flex items-center gap-1 rounded-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <Target className="h-4 w-4" aria-hidden="true" />
                Kyykky
              </Link>
              <Link
                to="/stats"
                className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium rounded-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                Tilastot
              </Link>
              <Link
                to="/health"
                className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium flex items-center gap-1 rounded-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <Heart className="h-4 w-4" aria-hidden="true" />
                Terveys
              </Link>
              <Link
                to="/weight"
                className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium flex items-center gap-1 rounded-sm focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <Scale className="h-4 w-4" aria-hidden="true" />
                Paino
              </Link>
            </nav>
          )}

          {/* User menu */}
          {isAuthenticated && user && (
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-3">
                {user.pictureUrl && (
                  <img
                    src={user.pictureUrl}
                    alt={user.name}
                    className="h-8 w-8 rounded-full"
                  />
                )}
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {user.name}
                </span>
              </div>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
                title={isDark ? 'Vaihda vaaleaan tilaan' : 'Vaihda tummaan tilaan'}
                aria-label={isDark ? 'Vaihda vaaleaan tilaan' : 'Vaihda tummaan tilaan'}
              >
                {isDark ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
              </button>
              <button
                onClick={handleLogout}
                className="btn btn-ghost text-gray-500 hover:text-gray-700 dark:text-gray-400 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 rounded-lg"
                title="Kirjaudu ulos"
                aria-label="Kirjaudu ulos"
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Mobile: Theme toggle and logout (menu replaced by BottomNav) */}
          {isAuthenticated && (
            <div className="md:hidden flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className="p-3 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors touch-manipulation focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
                aria-label={isDark ? 'Vaihda vaaleaan tilaan' : 'Vaihda tummaan tilaan'}
              >
                {isDark ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
              </button>
            </div>
          )}
        </div>

      </div>
    </header>
  );
}
